import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { useSEO } from '@/hooks/useSEO';
import { aboutInfo } from '@/mocks/comics';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const breadcrumbItems = [{ label: '关于我们' }];

  useSEO({
    title: '关于韩漫之家 - 品牌介绍 | 商务合作 | 联系我们',
    description: '韩漫之家成立于2019年，是国内领先的韩国漫画在线免费阅读平台。了解韩漫之家品牌故事、平台数据、商务合作方式及联系方式。',
    keywords: '韩漫之家, 关于我们, 品牌介绍, 商务合作, 联系我们, 韩国漫画平台',
    canonicalUrl: 'http://bferj.cn/about',
    ogTitle: '关于韩漫之家 - 品牌介绍 | 商务合作 | 联系我们',
    ogDescription: '韩漫之家成立于2019年，是国内领先的韩国漫画在线免费阅读平台。',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotEl?.value.trim()) {
      setFormStatus('success');
      return;
    }

    setIsSubmitting(true);
    setFormError('');

    try {
      setFormStatus('success');
      form.reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫之家',
    url: 'http://bferj.cn/',
    description: aboutInfo.introduction,
    foundingDate: aboutInfo.founded,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: aboutInfo.contact.email,
      telephone: `+86-${aboutInfo.contact.phone}`,
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CN',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      streetAddress: '望京SOHO T1座 1506室',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://bferj.cn/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫之家是什么时候成立的？',
        acceptedAnswer: { '@type': 'Answer', text: `韩漫之家成立于${aboutInfo.founded}年，是国内领先的韩国漫画在线免费阅读平台。` },
      },
      {
        '@type': 'Question',
        name: '如何与韩漫之家进行商务合作？',
        acceptedAnswer: { '@type': 'Answer', text: '您可以通过页面上的商务合作联系表单提交合作意向，或发送邮件至 hello@bferj.cn。我们支持广告投放、版权合作、内容授权等多种合作方式。' },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg data={organizationSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <SchemaOrg data={faqSchema} />

      <Navbar />

      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Page Title */}
          <div className="mt-8 mb-12 text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于韩漫之家
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {aboutInfo.tagline}
            </p>
          </div>

          {/* Brand Introduction */}
          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20digital%20art%20studio%20with%20large%20screens%20displaying%20Korean%20webtoon%20illustrations%2C%20creative%20workspace%20with%20warm%20lighting%2C%20minimalist%20interior%2C%20clean%20and%20professional%20atmosphere%2C%20artistic%20vibe&width=800&height=600&seq=about-brand-hanman&orientation=landscape"
                  alt="韩漫之家品牌形象"
                  title="韩漫之家 - 专业的韩国漫画阅读平台"
                  className="w-full rounded-lg object-cover"
                />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">品牌故事</h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  {aboutInfo.introduction}
                </p>
                <div className="p-4 bg-primary-50 rounded-lg border border-primary-100">
                  <h3 className="text-sm font-heading font-semibold text-primary-700 mb-2">我们的使命</h3>
                  <p className="text-sm text-foreground-700 leading-relaxed">{aboutInfo.mission}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="mb-16 py-12 bg-background-100 rounded-lg">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-10">
              平台数据
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 px-4">
              {aboutInfo.stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{stat.value}</div>
                  <div className="text-sm text-foreground-600 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Business Cooperation */}
          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">商务合作</h2>
            <p className="text-sm text-foreground-700 leading-relaxed mb-6">
              {aboutInfo.businessCooperation.description}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
              {aboutInfo.businessCooperation.types.map((type, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 p-3 bg-background-100 rounded-lg"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-check-line text-sm"></i>
                  </div>
                  <span className="text-sm text-foreground-800">{type}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Info + Form */}
          <section className="mb-16 grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">联系我们</h2>
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">联系邮箱</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-briefcase-line"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">商务合作邮箱</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.businessEmail}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-phone-line"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">客服电话</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-wechat-line"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">微信公众号</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">公司地址</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.address}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">商务合作咨询</h2>
              <form
                onSubmit={handleSubmit}
                className="bg-background-100 rounded-lg p-6 flex flex-col gap-4"
              >
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">姓名</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-3 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-3 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-800 mb-1.5">公司名称</label>
                  <input
                    id="contact-company"
                    name="company"
                    type="text"
                    className="w-full px-3 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的公司名称（选填）"
                  />
                </div>
                <div>
                  <label htmlFor="contact-type" className="block text-sm font-medium text-foreground-800 mb-1.5">合作类型</label>
                  <select
                    id="contact-type"
                    name="cooperation_type"
                    required
                    className="w-full px-3 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                  >
                    <option value="">请选择合作类型</option>
                    {aboutInfo.businessCooperation.types.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">合作说明</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-3 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请简要描述您的合作意向（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-500 mt-1 text-right">最多500字</p>
                </div>
                <input
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? '提交中...' : '提交合作意向'}
                </button>

                {formStatus === 'success' && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
                    提交成功！我们会尽快与您联系，感谢您的关注。
                  </div>
                )}
                {formStatus === 'error' && formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}
